/**
 * meetingsContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/
//import bluebird from "bluebird";
import * as React from "react";
import { Route, Routes, useNavigate } from "react-router-dom";

import { MeetingContext, HostControlsProvider } from "./meeting.context";
import { MeetingContainerWrapper } from "./meetingContainer.component";
import { MembersViewComponent } from "./membersView.component";

import { ClassToggles } from "../models/constants";
import { Meeting, WaitingDevice } from "../models/meeting";
import { SubscriberStream } from "../models/stream";

export function MeetingsContainer(): React.ReactElement<any, any> {
  const [classes, setClasses] = React.useState<ClassToggles>({
    chatContainer: false,
    showLeftNav: false,
    showRightDetail: false,
    multipleAccounts: false,
    withHuddle: false,
    hideChatInput: false
  });
  const [curMeeting, setCurMeeting] = React.useState<Meeting | null>(null);
  const [waitingDevices, setWaitingDevices] = React.useState<Array<WaitingDevice> | null>(null);
  const [streams, setStreams] = React.useState<Array<SubscriberStream>>([]);
  const [controlsProvider, setControlsProvider] = React.useState<HostControlsProvider | null>(null);
  const [muteNewStreams, setMuteNewStreams] = React.useState(false);
  const navigate = useNavigate();

  function hideLeftNav() {
    setClasses({ ...classes, showLeftNav: false });
  }

  function toggleRightDetail() {
    const currentlyShowing = classes.showRightDetail;
    setClasses({ ...classes, showRightDetail: !classes.showRightDetail });
    if (curMeeting && currentlyShowing) {
      setTimeout(() => {
        navigate(`/meeting/${curMeeting._id}/${curMeeting.token}`);
        setStreams(Array.from(streams)); // cause a redraw
      }, 500);
    }
  }

  function leaveMeeting() {
    if (classes.showRightDetail) {
      setClasses({ ...classes, showRightDetail: false });
    }
    navigate("/", { replace: true });
    setCurMeeting(null);
  }

  function toggleMeetingMembers() {
    if (!curMeeting) { return; }
    if (classes.showRightDetail) {
      navigate(`/meeting/${curMeeting._id}/${curMeeting.token}`);
      setClasses({ ...classes, showRightDetail: false });
      return;
    }
    navigate(`/meeting/${curMeeting._id}/${curMeeting.token}/members`);
    setClasses({ ...classes, showRightDetail: true });

    setTimeout(() => {
      setStreams(Array.from(streams));
    }, 500);
  }

  const classNames = "meetings-container " + Object.keys(classes).filter(cls => classes[cls as keyof ClassToggles])
    .join(" ");

  return <MeetingContext.Provider value={{
    muteNewStreams: muteNewStreams,
    setMuteNewStreams: setMuteNewStreams,
    meeting: curMeeting,
    setMeeting: setCurMeeting,
    waitingDevices: waitingDevices,
    setWaitingDevices: setWaitingDevices,
    streams: streams,
    setStreams: setStreams,
    leaveMeeting: leaveMeeting,
    toggleRightDetail: toggleRightDetail,
    toggleMembersView: toggleMeetingMembers,
    hostControlsProvider: controlsProvider,
    setHostControlsProvider: setControlsProvider
  }} >
    <div className={classNames}>
      <div className="background-overlay" onClick={hideLeftNav} />
      <Routes>
        <Route path=":meetingID/:meetingToken/members?" element={
          <MeetingContainerWrapper
            meeting={curMeeting}
            setMeeting={setCurMeeting}
            leaveMeeting={leaveMeeting} />
        } />
      </Routes>
      <aside className="right-detail trailing-side-container">
        <Routes>
          <Route path=":meetingID/:meetingToken/members" element={
            <MembersViewComponent streams={streams} setMuteNewStreams={setMuteNewStreams} />
          } />
        </Routes>
      </aside>
    </div>
  </MeetingContext.Provider>;
}
